import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from './Loading';
import '../styles/Timesheet.css';

function Timesheet() {
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);
  const [projects, setProjects] = useState([]);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [timesheetsByDate, setTimesheetsByDate] = useState({}); // { 'YYYY-MM-DD': [{...entry}] }
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD'
  const [isModalOpen, setIsModalOpen] = useState(false);

  // New entry form state (per modal)
  const [newEntry, setNewEntry] = useState({ subcontractorId: '', projectId: '' });
  // Staged entries (pending saves)
  const [stagedEntries, setStagedEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  // Fill range feature
  const [showFillRange, setShowFillRange] = useState(false);
  const [fillRangeStart, setFillRangeStart] = useState('');
  const [fillRangeEnd, setFillRangeEnd] = useState('');

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        // Fetch subcontractors
        const subSnapshot = await getDocs(collection(db, 'subcontractors'));
        const subList = [];
        subSnapshot.forEach((d) => subList.push({ id: d.id, ...d.data() }));
        setSubcontractors(subList);

        // Fetch projects (active only)
        const projectSnapshot = await getDocs(collection(db, 'projects'));
        const projectList = [];
        projectSnapshot.forEach((d) => {
          const p = d.data();
          if (!p.completionDate) projectList.push({ id: d.id, ...p });
        });
        setProjects(projectList);
      } catch (e) {
        console.error('Lookup fetch failed', e);
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchMonthEntries = async () => {
      setLoading(true);
      try {
        const start = new Date(currentMonth);
        const end = new Date(currentMonth);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0); // last day of month

        const startYMD = toYMD(start);
        const endYMD = toYMD(end);

        // Query timesheets by date within month range
        const qTimes = query(
          collection(db, 'timesheets'),
          where('date', '>=', startYMD),
          where('date', '<=', endYMD)
        );
        const snap = await getDocs(qTimes);
        const map = {};
        snap.forEach((d) => {
          const item = { id: d.id, ...d.data() };
          if (!map[item.date]) map[item.date] = [];
          map[item.date].push(item);
        });
        setTimesheetsByDate(map);
      } catch (e) {
        console.error('Month fetch failed', e);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthEntries();
  }, [currentMonth]);

  const days = useMemo(() => buildMonthDays(currentMonth), [currentMonth]);

  const openDayModal = (ymd) => {
    setSelectedDate(ymd);
    setIsModalOpen(true);
    setNewEntry({ subcontractorId: '', projectId: '' });
    setStagedEntries([]);
    setShowFillRange(false);
    setFillRangeStart(ymd);
    setFillRangeEnd(ymd);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDate(null);
    setNewEntry({ subcontractorId: '', projectId: '' });
    setStagedEntries([]);
    setShowFillRange(false);
    setFillRangeStart('');
    setFillRangeEnd('');
  };

  const addTimesheetEntry = () => {
    if (!selectedDate) return;
    const { subcontractorId, projectId } = newEntry;
    if (!subcontractorId || !projectId) {
      alert('Please select subcontractor and project.');
      return;
    }
    // Check if this combination already exists in staged entries
    const duplicate = stagedEntries.some(
      e => e.subcontractorId === subcontractorId && e.projectId === projectId
    );
    if (duplicate) {
      alert('This contractor and project combination is already in the list.');
      return;
    }
    // Add to staged entries
    const staged = {
      subcontractorId,
      projectId,
      hours: 1,
      date: selectedDate,
      tempId: Date.now(), // temporary ID for display
    };
    setStagedEntries([...stagedEntries, staged]);
    setNewEntry({ subcontractorId: '', projectId: '' });
  };

  const removeStagedEntry = (tempId) => {
    setStagedEntries(stagedEntries.filter(e => e.tempId !== tempId));
  };

  // Generate array of dates between start and end (inclusive), ensuring no duplicates
  const generateDateRange = (startYMD, endYMD) => {
    const dates = [];
    const start = new Date(startYMD);
    const end = new Date(endYMD);
    
    // Ensure start is before or equal to end
    if (start > end) {
      return [];
    }
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const ymd = toYMD(new Date(d));
      // Ensure no duplicates
      if (!dates.includes(ymd)) {
        dates.push(ymd);
      }
    }
    return dates;
  };

  const saveAllStagedEntries = async (dateRange = null) => {
    if (stagedEntries.length === 0) return;
    if (saving) return; // Prevent double-saving
    
    const datesToFill = dateRange ? generateDateRange(dateRange.start, dateRange.end) : [selectedDate];
    if (datesToFill.length === 0) return;

    try {
      setSaving(true);
      const allPromises = [];
      
      // For each date in the range
      datesToFill.forEach(date => {
        // For each staged entry
        stagedEntries.forEach(staged => {
          const payload = {
            subcontractorId: staged.subcontractorId,
            projectId: staged.projectId,
            hours: staged.hours,
            date: date,
            createdAt: new Date().toISOString(),
          };
          allPromises.push(
            addDoc(collection(db, 'timesheets'), payload).then(ref => ({ id: ref.id, ...payload, date }))
          );
        });
      });
      
      const saved = await Promise.all(allPromises);
      
      // Update local state - group by date, avoiding duplicates
      setTimesheetsByDate((prev) => {
        const updated = { ...prev };
        saved.forEach(entry => {
          if (!updated[entry.date]) {
            updated[entry.date] = [];
          }
          // Check if this entry already exists (by ID) to avoid duplicates
          const exists = updated[entry.date].some(existing => existing.id === entry.id);
          if (!exists) {
            updated[entry.date].push(entry);
          }
        });
        return updated;
      });
      
      setStagedEntries([]);
      setShowFillRange(false);
      setFillRangeStart(selectedDate);
      setFillRangeEnd(selectedDate);
      
      if (datesToFill.length > 1) {
        alert(`Successfully saved entries to ${datesToFill.length} days!`);
      }
    } catch (e) {
      console.error('Save all failed', e);
      alert('Failed to save entries');
    } finally {
      setSaving(false);
    }
  };

  const handleFillRange = () => {
    if (!fillRangeStart || !fillRangeEnd) {
      alert('Please select both start and end dates');
      return;
    }
    if (stagedEntries.length === 0) {
      alert('Please add entries to fill first');
      return;
    }
    saveAllStagedEntries({ start: fillRangeStart, end: fillRangeEnd });
  };

  const removeTimesheetEntry = async (entryId) => {
    if (!selectedDate) return;
    try {
      await deleteDoc(doc(db, 'timesheets', entryId));
      setTimesheetsByDate((prev) => {
        const list = (prev[selectedDate] || []).filter((x) => x.id !== entryId);
        return { ...prev, [selectedDate]: list };
      });
    } catch (e) {
      console.error('Delete entry failed', e);
      alert('Failed to delete entry');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="timesheet-container">
      <div className="timesheet-header">
        <div className="timesheet-header-left">
          <Link to="/dashboard" className="home-button">
            ← Home
          </Link>
          <h1>Timesheet</h1>
        </div>
        <div className="month-switcher">
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>&lt;</button>
          <div className="month-label">{formatMonthYear(currentMonth)}</div>
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>&gt;</button>
        </div>
      </div>

      <div className="calendar">
        <div className="calendar-grid calendar-headings">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
            <div key={d} className="calendar-heading">{d}</div>
          ))}
        </div>
        <div className="calendar-grid">
          {days.map((day, idx) => {
            const ymd = day ? toYMD(day) : null;
            const entries = ymd ? (timesheetsByDate[ymd] || []) : [];
            
            // Group entries by project
            const projectsByDate = {};
            entries.forEach((entry) => {
              const projectId = entry.projectId;
              if (!projectsByDate[projectId]) {
                projectsByDate[projectId] = {
                  id: projectId,
                  name: resolveName(projects, projectId, 'name'),
                  count: 0
                };
              }
              projectsByDate[projectId].count++;
            });
            const projectList = Object.values(projectsByDate);
            
            return (
              <div 
                key={idx} 
                className={`calendar-cell ${day ? '' : 'empty'}`}
                onClick={() => day && openDayModal(ymd)}
              >
                {day && (
                  <>
                    <div className="cell-date">{day.getDate()}</div>
                    {projectList.length > 0 && (
                      <div className="cell-badges">
                        {projectList.map((project) => {
                          const colorScheme = getProjectColor(project.id);
                          return (
                            <div
                              key={project.id}
                              className="cell-badge"
                              style={{
                                backgroundColor: colorScheme.bg,
                                borderColor: colorScheme.border,
                                color: colorScheme.text,
                              }}
                            >
                              {project.name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isModalOpen && (
        <div className="ts-modal-overlay" onClick={closeModal}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h2>Entries for {selectedDate}</h2>
              <button className="ts-close" onClick={closeModal}>×</button>
            </div>

            <div className="ts-existing-entries">
              {(timesheetsByDate[selectedDate] || []).length === 0 && stagedEntries.length === 0 ? (
                <p className="ts-empty">No entries yet for this day.</p>
              ) : (
                <>
                  {/* Saved entries */}
                  {(timesheetsByDate[selectedDate] || []).length > 0 && (
                    <div className="ts-entry-section">
                      <h4 className="ts-section-title">Saved Entries</h4>
                      <div className="ts-entry-list">
                        {(timesheetsByDate[selectedDate] || []).map((e) => (
                          <div key={e.id} className="ts-entry-row">
                            <div className="ts-entry-col">
                              {resolveName(subcontractors, e.subcontractorId, 'name')}
                            </div>
                            <div className="ts-entry-col">
                              {resolveName(projects, e.projectId, 'name')}
                            </div>
                            <div className="ts-entry-actions">
                              <button className="ts-delete" onClick={() => removeTimesheetEntry(e.id)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Staged entries */}
                  {stagedEntries.length > 0 && (
                    <div className="ts-entry-section">
                      <h4 className="ts-section-title">Pending Entries ({stagedEntries.length})</h4>
                      <div className="ts-entry-list">
                        {stagedEntries.map((e) => (
                          <div key={e.tempId} className="ts-entry-row ts-entry-staged">
                            <div className="ts-entry-col">
                              {resolveName(subcontractors, e.subcontractorId, 'name')}
                            </div>
                            <div className="ts-entry-col">
                              {resolveName(projects, e.projectId, 'name')}
                            </div>
                            <div className="ts-entry-actions">
                              <button className="ts-delete" onClick={() => removeStagedEntry(e.tempId)}>Remove</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="ts-form">
              <div className="ts-form-row">
                <div className="ts-field">
                  <label>Contractor</label>
                  <select
                    value={newEntry.subcontractorId}
                    onChange={(e) => setNewEntry({ ...newEntry, subcontractorId: e.target.value })}
                  >
                    <option value="">Select contractor</option>
                    {subcontractors.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="ts-field">
                  <label>Project</label>
                  <select
                    value={newEntry.projectId}
                    onChange={(e) => setNewEntry({ ...newEntry, projectId: e.target.value })}
                  >
                    <option value="">Select project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {stagedEntries.length > 0 && (
                <div className="ts-fill-range-section">
                  <button 
                    className="ts-toggle-fill-range" 
                    onClick={() => setShowFillRange(!showFillRange)}
                    type="button"
                  >
                    {showFillRange ? '▼' : '▶'} Fill Multiple Days
                  </button>
                  {showFillRange && (
                    <div className="ts-range-picker">
                      <div className="ts-range-inputs">
                        <div className="ts-field">
                          <label>From Date</label>
                          <input
                            type="date"
                            value={fillRangeStart}
                            onChange={(e) => setFillRangeStart(e.target.value)}
                          />
                        </div>
                        <div className="ts-field">
                          <label>To Date</label>
                          <input
                            type="date"
                            value={fillRangeEnd}
                            onChange={(e) => setFillRangeEnd(e.target.value)}
                            min={fillRangeStart}
                          />
                        </div>
                      </div>
                      <div className="ts-range-info">
                        {fillRangeStart && fillRangeEnd && fillRangeStart <= fillRangeEnd && (
                          <span className="ts-range-count">
                            Will fill {generateDateRange(fillRangeStart, fillRangeEnd).length} day(s)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="ts-actions">
                <button className="ts-add-to-list" onClick={addTimesheetEntry}>
                  Add to List
                </button>
                {stagedEntries.length > 0 && (
                  <>
                    <button 
                      className="ts-save-all" 
                      onClick={() => saveAllStagedEntries()} 
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : `Save This Day (${stagedEntries.length})`}
                    </button>
                    {showFillRange && fillRangeStart && fillRangeEnd && fillRangeStart <= fillRangeEnd && (
                      <button 
                        className="ts-fill-range-btn" 
                        onClick={handleFillRange} 
                        disabled={saving}
                      >
                        {saving ? 'Filling...' : `Fill Range (${generateDateRange(fillRangeStart, fillRangeEnd).length} days)`}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildMonthDays(firstOfMonth) {
  const first = new Date(firstOfMonth);
  const year = first.getFullYear();
  const month = first.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  // Determine start padding (Mon=0 .. Sun=6 for our grid headings)
  const jsDay = start.getDay(); // Sun=0..Sat=6
  const padLeft = (jsDay + 6) % 7; // convert to Mon-first index
  const totalDays = end.getDate();
  const cells = [];
  for (let i = 0; i < padLeft; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  // pad to complete weeks (multiples of 7)
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatMonthYear(date) {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function addMonths(date, delta) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + delta);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function resolveName(list, id, field) {
  const item = list.find((x) => x.id === id);
  return item ? item[field] : id;
}

// Generate a consistent color for a project ID using a hash function
function getProjectColor(projectId) {
  const colors = [
    { bg: '#eef6ff', border: '#2563eb', text: '#1e40af' }, // Blue
    { bg: '#f0fdf4', border: '#16a34a', text: '#15803d' }, // Green
    { bg: '#fef3c7', border: '#f59e0b', text: '#d97706' }, // Yellow/Orange
    { bg: '#fce7f3', border: '#ec4899', text: '#be185d' }, // Pink
    { bg: '#ede9fe', border: '#8b5cf6', text: '#6d28d9' }, // Purple
    { bg: '#e0f2fe', border: '#0ea5e9', text: '#0284c7' }, // Sky
    { bg: '#dcfce7', border: '#22c55e', text: '#16a34a' }, // Emerald
    { bg: '#fef3c7', border: '#eab308', text: '#ca8a04' }, // Amber
    { bg: '#fae8ff', border: '#d946ef', text: '#a21caf' }, // Fuchsia
    { bg: '#cffafe', border: '#06b6d4', text: '#0891b2' }, // Cyan
  ];
  
  // Simple hash function to consistently assign colors
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = ((hash << 5) - hash) + projectId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return colors[Math.abs(hash) % colors.length];
}

export default Timesheet; 